// Generated by TaroBase SDK Generator: 2026-08-23T03:59:35.511Z
// Collection: purchases/$purchaseId
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * Purchase records that trigger an atomic onchain SKR token split: 90% to the wallpaper creator and 10% to the project admin. No tokens are burned. Uses isPassthrough so both transfers execute onchain but purchase data is stored offchain only.
 */

export interface PurchasesRequest {
  buyer: AddressType;
  wallpaperId: string;
  amt: number | TimeOperation | IncrementOperation | TokenAmount;
  createdAt: number | TimeOperation | IncrementOperation | TokenAmount;
  creator: AddressType;
}

export interface PurchasesResponse {
  buyer: string;
  wallpaperId: string;
  amt: number;
  createdAt: number;
  creator: string;
  id: string;
  tarobase_created_at: number;
  tarobase_transaction_hash?: string | undefined; // Represents the hash of the transaction that created this item. Can be used to create Solscan links (combine with the current network in the Tarobase app)
}

/**
 * Build a Purchases operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildPurchases(purchaseId: string, data?: PurchasesRequest): DocumentOperation {
  const doc = data ? transformValues(data) : {};
  return { path: `purchases/${purchaseId}`, document: doc };
}

/**
 * Authenticated users can create a purchase. The buyer field must match the caller wallet address. The amt field MUST be at least SKR_MIN_PRICE_RAW (1000000 = 1 SKR). The creator field is the wallpaper uploader's wallet (Address!, immutable) and must be supplied by the client; the backend API (PROJECT_VAULT_ADDRESS) independently verifies that creator == wallpapers/$wallpaperId.uploadedBy AND that amt equals the wallpaper's price before recording the purchase in users/$userId/purchases/$wallpaperId. Cross-collection lookups in the policy rule itself are not possible because purchases is onchain and wallpapers is offchain (onchain collections cannot read offchain documents). On creation, two atomic SKR @TokenPlugin.transfer calls execute in the same on-chain transaction: 90% of amt to creator, and the remainder (amt - 90%, the entire rest including any rounding dust) to ADMIN_ADDRESS. No tokens are burned. Using subtraction for the last leg guarantees no dust is left in the buyer's wallet. isPassthrough means data is stored offchain only. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setPurchases(purchaseId: string, data?: PurchasesRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data || {});
    const succeeded = await set(`purchases/${purchaseId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting Purchases: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Anyone can read purchase records (onchain requires public read).
   (Get Single Item)
 */
export async function getPurchases(purchaseId: string): Promise<PurchasesResponse | null> {
  try {
    const result = await get(`purchases/${purchaseId}`);
    return result as PurchasesResponse | null;
  } catch (error) {
    console.error(`Error getting Purchases: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single Purchases document. (
  Read Operation Details: Anyone can read purchase records (onchain requires public read).
  )
 */
export function subscribePurchases(callback: (data: PurchasesResponse | null) => void, purchaseId: string): Promise<() => Promise<void>> {
  return subscribe(`purchases/${purchaseId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as PurchasesResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to Purchases at path '${`purchases/${purchaseId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many Purchases items from collection purchases
 
  Read Operation Details: Anyone can read purchase records (onchain requires public read).
  
 */
export async function getManyPurchases(filter: string = ""): Promise<PurchasesResponse[]> {
  try {
    const results = await get("purchases", { prompt: filter });
    return Array.isArray(results) ? results as PurchasesResponse[] : [];
  } catch (error) {
    console.error(`Error getting Purchases collection at path 'purchases': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in Purchases collection at purchases
 
  Read Operation Details: Anyone can read purchase records (onchain requires public read).
  
 */
export function subscribeManyPurchases(callback: (data: PurchasesResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("purchases", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as PurchasesResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to Purchases collection at path '${"purchases"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all Purchases items from collection purchases
 
  Read Operation Details: Anyone can read purchase records (onchain requires public read).
  
 */
export async function getAllPurchases(filter: string = ""): Promise<PurchasesResponse[]> {
  try {
    const results = await get("purchases", { prompt: filter });
    return Array.isArray(results) ? results as PurchasesResponse[] : [];
  } catch (error) {
    console.error(`Error getting Purchases collection at path 'purchases': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in Purchases collection at purchases
 
  Read Operation Details: Anyone can read purchase records (onchain requires public read).
  
 */
export function subscribeAllPurchases(callback: (data: PurchasesResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("purchases", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as PurchasesResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to Purchases collection at path '${"purchases"}': ${formatError(error)}`);
    }
  });
}

/**
 * Count Purchases items in collection purchases.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countPurchases(filter: string = ""): Promise<AggregateResult> {
  try {
    return await count("purchases", { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting Purchases at path 'purchases': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on Purchases items in collection purchases.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregatePurchases(operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate("purchases", operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating Purchases at path 'purchases': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Runs the "getBuyerSkrBalance" query on Purchases.
 * Query Logic: @TokenPlugin.getBalance(@user.address, @constants.SKR_TOKEN_MINT)
 */
export async function runGetBuyerSkrBalanceQueryForPurchases(purchaseId: string, ): Promise<number> {
  try {
    const result = await runQuery(`purchases/${purchaseId}`, "getBuyerSkrBalance", {});
    return (typeof result === "bigint" ? Number(result) : Number(result ?? 0));
  } catch (error) {
    console.error(`Error running query "getBuyerSkrBalance" on Purchases: ${formatError(error)}`);
    throw error;
  }
}

